import React, { useEffect, useReducer, useState } from 'react';

import { useStyles } from './Themable.hooks';

import {
    clearEmptyAssignments,
    fetchProductAssignments,
    patchProductAssignments,
} from '../../../../features/Products/state';
import {
    useProductAssignments,
    useProductAssignmentsStatus,
    useProductEmptyAssignments,
} from '../../../../features/Products/state/index.hooks';

import { useAppDispatch } from '../../../../hooks';

import TranslationHelper from '../../../../helpers/TranslationHelper';
import { sortAlphabetically } from '../../../../helpers/comparators';

import {
    filterObjects,
    findIndexById,
    not,
    union,
} from '../../../../features/Products/helpers/filterObjects';

import DialogTitle from '../../../dialogs/DialogTitle';
import LoadingSpinner from '../../../loadingSpinner/LoadingSpinner';
import { IListObjectInitialState } from '../../../TransferList/CommonTransferList';
import useToggle from '../../../../hooks/useToggle';
import LeftList from '../../../../features/Products/components/ProductTransferList/LeftList';
import RightList from '../../../../features/Products/components/ProductTransferList/RightList';
import { Dialog, DialogContent } from '@mui/material';
import { hideGlobalDialog } from '../../../../state/ui/dialog';
import useAsyncThunkWithSnackbar from '../../../../hooks/useAsyncThunkWithSnackbar';
import ProductTransferList from '../../../../features/Products/components/ProductTransferList/ProductTransferList';
import {
    IProduct,
    IProductAssignment,
    IProductFilterInitialState,
} from '../../../../features/Products/types';

interface IOwnProps {
    selectedProduct: IProduct;
}

const ProductAssignmentDialog = ({ selectedProduct }: IOwnProps) => {
    const classes = useStyles();

    const dispatch = useAppDispatch();

    const emptyAssignments = useProductEmptyAssignments();
    const assignmentsStatus = useProductAssignmentsStatus();
    const assignments = useProductAssignments();
    const { isOpen, handleToggle: handleToggleClose } = useToggle(true);

    const [added, setAdded] = useState<IProductAssignment[]>([]);
    const [removed, setRemoved] = useState<IProductAssignment[]>([]);
    const [checked, setChecked] = useState<IProductAssignment[]>([]);

    const filterInitialState: IProductFilterInitialState = {
        nameUnassigned: '',
        nameAssigned: '*',
    };
    const handleAsyncRequest = useAsyncThunkWithSnackbar();

    const listObjectsInitialState: IListObjectInitialState<IProductAssignment> =
        {
            unassignedObjects: emptyAssignments,
            assignedObjects: assignments,
            filteredAssignedObjects: assignments,
            originalAssignedObjects: [],
        };

    const [filter, setFilter] = useReducer(
        (
            curVals: IProductFilterInitialState,
            newVals: Partial<IProductFilterInitialState>
        ) => ({ ...curVals, ...newVals }),
        filterInitialState
    );

    const [objectLists, setObjectLists] = useReducer(
        (
            curVals: IListObjectInitialState<IProductAssignment>,
            newVals: Partial<IListObjectInitialState<IProductAssignment>>
        ) => ({
            ...curVals,
            ...newVals,
        }),
        listObjectsInitialState
    );

    useEffect(() => {
        setObjectLists({
            unassignedObjects: filterObjects(
                handleAddedAndRemoved(emptyAssignments ?? []),
                filter.nameUnassigned
            ),
        });
    }, [emptyAssignments]);

    useEffect(() => {
        handleAsyncRequest({
            asyncAction: fetchProductAssignments({
                id: selectedProduct.id,
            }),
            onSuccess: {
                callback: (data) =>
                    setObjectLists({ originalAssignedObjects: data }),
            },
            onError: {
                callback: () => handleCloseDialog(),
            },
        });
    }, [selectedProduct.id]);

    useEffect(() => {
        setObjectLists({
            filteredAssignedObjects: assignments ?? [],
            assignedObjects: assignments ?? [],
        });
    }, [assignments]);

    const handleToggle = (value: IProductAssignment) => () => {
        const currentIndex = findIndexById(checked, value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const handleCloseDialog = (event?: object, reason?: string) => {
        if (reason === 'backdropClick') {
            return;
        }
        dispatch(clearEmptyAssignments());
        dispatch(hideGlobalDialog());
        handleToggleClose();
    };

    const handleAddedAndRemoved = (arr: IProductAssignment[]) => {
        const removeAdded = not(arr, added);
        return union(
            removeAdded.sort((a, b) => sortAlphabetically(a.name, b.name)),
            removed
        );
    };

    const handleSubmit = () => {
        handleAsyncRequest({
            asyncAction: patchProductAssignments({
                id: selectedProduct.id,
                data: { added, removed },
            }),
            onSuccess: {
                message: TranslationHelper.translate('Changes have been saved'),
            },
            onFinally: {
                callback: () => handleCloseDialog(),
            },
        });
    };

    const leftList = (
        <LeftList
            objects={objectLists}
            checked={checked}
            setChecked={setChecked}
            filter={filter}
            setFilter={setFilter}
            handleToggle={handleToggle}
        />
    );

    const rightList = (
        <RightList
            objects={objectLists}
            setObjects={setObjectLists}
            filter={filter}
            setFilter={setFilter}
            checked={checked}
            setChecked={setChecked}
            handleToggle={handleToggle}
        />
    );

    return (
        <Dialog
            open={isOpen}
            onClose={handleCloseDialog}
            maxWidth={'lg'}
            classes={{ paper: classes.paper }}
        >
            <DialogTitle
                title={`${TranslationHelper.translate(
                    'Assigning objects to product'
                )}: "${selectedProduct.name}"`}
                close={handleCloseDialog}
                save={handleSubmit}
                loading={assignmentsStatus === 'loading'}
            ></DialogTitle>
            <DialogContent>
                <div className={classes.wrapper}>
                    {assignmentsStatus === 'loading' ? (
                        <LoadingSpinner
                            size="50"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                        />
                    ) : (
                        <ProductTransferList
                            objectLists={objectLists}
                            setObjectLists={setObjectLists}
                            added={added}
                            setAdded={setAdded}
                            removed={removed}
                            setRemoved={setRemoved}
                            filter={filter}
                            customComponents={{ leftList, rightList }}
                            checked={checked}
                            setChecked={setChecked}
                            filterObjects={filterObjects}
                        />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProductAssignmentDialog;
